import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import AuthContext from "../Context/AuthContext";

// küçük debounce hook'u
function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function initialsOf(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "U";
}

export default function UserUpdate(props) {
  const { loggedIn } = useContext(AuthContext);
  const history = useHistory();

  const userId = props.match?.params?.id;

  // temel state
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(""); // optional

  // accounts
  const [accounts, setAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]); // account objeleri
  const [searchTerm, setSearchTerm] = useState("");
  const debounced = useDebounced(searchTerm, 200);

  // ui
  const [isLoading, setIsLoading] = useState(true);
  const [fetchErr, setFetchErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [fieldErr, setFieldErr] = useState("");

  // fetch accounts
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get("https://act-api.vercel.app/accounts");
        if (!mounted) return;
        setAccounts(Array.isArray(data) ? data : []);
      } catch {
        /* sessiz geç */
      }
    })();
    return () => { mounted = false; };
  }, []);

  // fetch user
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setIsLoading(true);
        setFetchErr("");
        const { data } = await axios.post("https://act-api.vercel.app/finduser", { id: userId });
        if (!mounted) return;
        setData(data || {});
        setName(data?.name || "");
        setEmail(data?.eMail || "");
        // gelen account alanı objeler ise direkt ata; id listesi ise şimdilik olduğu gibi tut
        setSelectedAccounts(Array.isArray(data?.account) ? data.account : []);
      } catch (e) {
        if (!mounted) return;
        setFetchErr("Kullanıcı bilgileri yüklenemedi. Lütfen tekrar deneyin.");
      } finally {
        mounted && setIsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [userId]);

  // Eğer backend user.account’u id dizisi döndürürse, accounts geldikten sonra objelere eşle
  useEffect(() => {
    if (!accounts.length || !Array.isArray(selectedAccounts) || !selectedAccounts.length) return;
    // eğer selectedAccounts elemanları string/primitive ise obje’ye map’le
    if (typeof selectedAccounts[0] !== "object") {
      const mapped = selectedAccounts
        .map((id) => accounts.find((a) => a._id === id))
        .filter(Boolean);
      if (mapped.length) setSelectedAccounts(mapped);
    }
  }, [accounts]); // intentionally only on accounts change

  // arama filtresi
  const filteredAccounts = useMemo(() => {
    const term = debounced.trim().toLowerCase();
    const base = accounts || [];
    const selIds = new Set(selectedAccounts.map((a) => a?._id));
    const list = term
      ? base.filter(
          (a) =>
            (a?.name || "").toLowerCase().includes(term) ||
            (a?.company || "").toLowerCase().includes(term)
        )
      : base.slice(0, 20);
    // seçilmiş olanları listede yine de gösterelim ama işaretlenmiş olsun; istersen tamamen gizleyebilirim
    return list.slice(0, 30).map((a) => ({ ...a, __selected: selIds.has(a._id) }));
  }, [accounts, debounced, selectedAccounts]);

  function addAccount(acc) {
    if (!acc?._id) return;
    if (selectedAccounts.some((a) => a?._id === acc._id)) return;
    setSelectedAccounts((prev) => [...prev, acc]);
  }
  function removeAccount(acc) {
    setSelectedAccounts((prev) => prev.filter((a) => a?._id !== acc?._id));
  }

  // doğrulama
  function validate() {
    if (!name.trim()) return "User Name is required.";
    if (!email.trim()) return "Email is required.";
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    if (!emailOk) return "Please enter a valid email address.";
    if (password && password.length < 6) return "Password must be at least 6 characters.";
    return "";
  }

  async function submit(e) {
    e.preventDefault();
    setSuccess(false);
    setFieldErr("");
    const v = validate();
    if (v) {
      setFieldErr(v);
      return;
    }
    try {
      setSaving(true);
      const payload = {
        id: userId,
        name,
        email,
        selectedAccounts, // backend daha önce obje kabul ediyordu
      };
      if (password) payload.password = password; // boş ise gönderme
      const res = await axios.post("https://act-api.vercel.app/userupdate", payload);
      if (res.data?.success) setSuccess(true);
      else setFieldErr("Güncelleme sırasında beklenmeyen bir hata oluştu.");
    } catch (error) {
      const t = error?.response?.data;
      if (t?.ErrorType === "Field") {
        setFieldErr(t?.ErrorMessage || "Alan hatası oluştu.");
      } else {
        setFieldErr("Güncelleme başarısız oldu.");
      }
    } finally {
      setSaving(false);
    }
  }

  async function removeUser() {
    try {
      setRemoving(true);
      await axios.post("https://act-api.vercel.app/removeuser", { id: userId });
      history.push("/yourteam");
    } catch {
      setRemoving(false);
      setFieldErr("Kullanıcı silinemedi. Lütfen tekrar deneyin.");
      setRemoveOpen(false);
    }
  }

  if (loggedIn === false) {
    return (
      <div className="min-h-screen grid place-items-center bg-gradient-to-b from-slate-50 to-slate-100">
        <div className="text-center">
          <p className="text-slate-700 font-semibold text-lg">You are not logged in.</p>
          <p className="text-slate-500 mt-2">
            Go to{" "}
            <a href="/login" className="text-blue-600 underline">
              Login
            </a>{" "}
            page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-800">
      {/* Üst Başlık */}
      <div className="sticky top-0 z-10 bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/50 border-b border-slate-200">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <h1 className="text-lg font-semibold truncate">Edit User</h1>
            <p className="text-xs text-slate-500 truncate">{data?.name || "—"}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setRemoveOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 focus:ring-4 focus:ring-red-100"
            >
              Remove User
            </button>
            <button
              onClick={submit}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 disabled:opacity-60"
            >
              {saving ? "Saving…" : "Update"}
            </button>
          </div>
        </div>
      </div>

      {/* İçerik */}
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
        {fetchErr && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {fetchErr}
          </div>
        )}
        {success && (
          <div className="mb-6 rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700">
            You have successfully updated the information.
          </div>
        )}
        {fieldErr && (
          <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            {fieldErr}
          </div>
        )}

        {isLoading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-slate-200 animate-pulse" />
              <div className="flex-1">
                <div className="h-4 w-2/3 bg-slate-200 rounded animate-pulse mb-2" />
                <div className="h-3 w-1/3 bg-slate-200 rounded animate-pulse" />
              </div>
            </div>
            <div className="mt-6 space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-10 bg-slate-200 rounded animate-pulse" />
              ))}
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            {/* üst: avatar */}
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full ring-2 ring-slate-100 bg-slate-100 overflow-hidden flex items-center justify-center">
                <span className="text-slate-600 font-semibold">{initialsOf(name || data?.name || "")}</span>
              </div>
              <div className="min-w-0">
                <h2 className="font-semibold">{name || data?.name || "User"}</h2>
                <p className="text-xs text-slate-500 truncate">{email || data?.eMail || "—"}</p>
              </div>
            </div>

            {/* alanlar */}
            <div className="mt-6 grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  User Name <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
                  placeholder={data?.name || "User Name"}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  E-Mail <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
                  placeholder={data?.eMail || "user@example.com"}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  autoComplete="email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Password <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  autoComplete="new-password"
                />
                <p className="mt-1 text-xs text-slate-500">Boş bırakırsanız parola değişmez.</p>
              </div>

              {/* Accounts seçimi */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Accounts</label>

                <div className="relative">
                  <input
                    className="w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
                    type="text"
                    placeholder="Search accounts by name or company…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                    />
                  </svg>
                </div>

                <ul className="mt-3 max-h-56 overflow-auto rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                  {filteredAccounts.length === 0 ? (
                    <li className="p-3 text-sm text-slate-500">No accounts found.</li>
                  ) : (
                    filteredAccounts.map((acc) => (
                      <li
                        key={acc._id}
                        className="p-3 text-sm hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                        onClick={() => (acc.__selected ? removeAccount(acc) : addAccount(acc))}
                        title={acc.__selected ? "Remove from selection" : "Add to selection"}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-8 w-8 rounded-full bg-slate-100 ring-1 ring-slate-200 overflow-hidden flex items-center justify-center">
                            {acc.imageURL ? (
                              <img
                                src={acc.imageURL}
                                alt={acc.name}
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  e.currentTarget.parentElement.textContent = initialsOf(acc.name);
                                }}
                              />
                            ) : (
                              <span className="text-xs text-slate-600 font-semibold">
                                {initialsOf(acc.name)}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-slate-800 truncate">{acc.name}</div>
                            <div className="text-xs text-slate-500 truncate">{acc.company || "—"}</div>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          readOnly
                          checked={!!acc.__selected}
                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                      </li>
                    ))
                  )}
                </ul>

                {selectedAccounts.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {selectedAccounts.map((acc) => (
                      <span
                        key={acc._id || acc.name}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700"
                      >
                        {acc.name}
                        <button
                          type="button"
                          onClick={() => removeAccount(acc)}
                          className="rounded-full p-0.5 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
                          aria-label={`Remove ${acc.name}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* actions */}
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setRemoveOpen(true)}
                className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 focus:ring-4 focus:ring-red-100"
              >
                Remove User
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 disabled:opacity-60"
              >
                {saving ? "Saving…" : "Update"}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Remove confirm modal */}
      {removeOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          onClick={() => setRemoveOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold">Remove user?</h3>
            <p className="mt-2 text-sm text-slate-600">
              Bu kullanıcıyı silmek geri alınamaz. Emin misiniz?
            </p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => setRemoveOpen(false)}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={removeUser}
                disabled={removing}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-4 focus:ring-red-200 disabled:opacity-60"
              >
                {removing ? "Removing…" : "Yes, remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
